import type { Path } from "history";
import { useMemo } from "react";
import { t } from "ttag";

import { UserAvatar } from "metabase/common/components/UserAvatar";
import { useSetting } from "metabase/common/hooks";
import { PLUGIN_IS_PASSWORD_USER } from "metabase/plugins";
import { Box, Flex, Tabs, Title, rem } from "metabase/ui";
import { getFullName } from "metabase/utils/user";
import type { User } from "metabase-types/api";

import S from "./AccountHeader.module.css";

type AccountHeaderProps = {
  user: User;
  path?: string;
  onChangeLocation?: (nextLocation: Path) => void;
};

export const AccountHeader = ({
  user,
  path,
  onChangeLocation,
}: AccountHeaderProps) => {
  const hasPasswordChange = useMemo(
    () => PLUGIN_IS_PASSWORD_USER.every((predicate) => predicate(user)),
    [user],
  );
  const mfaEnforcement = useSetting("mfa-enforcement");
  const isMfaEnabled = mfaEnforcement != null && mfaEnforcement !== "off";
  const hasSecurityTab =
    isMfaEnabled && (hasPasswordChange || user.sso_source === "ldap");

  const tabs = useMemo(
    () => [
      { name: t`Profile`, value: "/account/profile" },
      ...(hasPasswordChange
        ? [{ name: t`Password`, value: "/account/password" }]
        : []),
      ...(hasSecurityTab
        ? [{ name: t`Security`, value: "/account/security" }]
        : []),
      { name: t`Login History`, value: "/account/login-history" },
      { name: t`Notifications`, value: "/account/notifications" },
    ],
    [hasPasswordChange, hasSecurityTab],
  );

  const userFullName = getFullName(user);

  return (
    <Flex
      className={S.root}
      data-testid="account-header"
      direction="column"
      justify="center"
      align="center"
      bg="background_page-primary"
      pt={{ base: "sm", sm: "md" }}
    >
      <Flex direction="column" align="center" p={{ base: "md", md: rem(64) }}>
        <Box mb={{ base: "sm", sm: "md" }}>
          <UserAvatar user={user} className={S.avatar} />
        </Box>
        {userFullName && (
          <Title order={2} fz="md" ta="center" mb="xs">
            {userFullName}
          </Title>
        )}
        <Title order={3} fz="md" fw="normal" ta="center" c="text-secondary">
          {user.email}
        </Title>
      </Flex>
      <Tabs
        listBorder={false}
        value={path ?? null}
        onChange={(value) => value && onChangeLocation?.(value)}
      >
        <Tabs.List>
          {tabs.map((tab) => (
            <Tabs.Tab key={tab.value} value={tab.value}>
              {tab.name}
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Tabs>
    </Flex>
  );
};
